import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type AvailableField,
  type BranchCase,
  type BranchNodeData,
  type BranchOp,
  makeId,
} from "@/lib/productGraph";

interface Props {
  data: BranchNodeData;
  onChange: (patch: Partial<BranchNodeData>) => void;
  availableFields: AvailableField[];
}

const OPS: BranchOp[] = ["equals", "gt", "gte", "lt", "lte"];
const NONE = "__none__";

export function BranchEditor({ data, onChange, availableFields }: Props): JSX.Element {
  const { t } = useTranslation();
  const cases = data.cases ?? [];
  const selectedField = availableFields.find((f) => f.key === data.field);
  const isEnum = selectedField?.type === "select";
  const options = selectedField?.options ?? [];

  function addCase(): void {
    const newCase: BranchCase = {
      id: makeId("case"),
      op: "equals",
      value: "",
      label: "",
    };
    onChange({ cases: [...cases, newCase] });
  }

  function updateCase(idx: number, patch: Partial<BranchCase>): void {
    onChange({ cases: cases.map((c, i) => (i === idx ? { ...c, ...patch } : c)) });
  }

  function removeCase(idx: number): void {
    onChange({ cases: cases.filter((_, i) => i !== idx) });
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label className="text-xs">{t("enterpriseProductBuilder.nodeLabelLabel")}</Label>
        <Input
          value={data.label ?? ""}
          onChange={(e) => onChange({ label: e.target.value })}
        />
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs">{t("enterpriseProductBuilder.branchFieldLabel")}</Label>
        {availableFields.length === 0 ? (
          <p className="rounded-md border border-dashed py-3 text-center text-xs text-muted-foreground">
            {t("enterpriseProductBuilder.branchNoFieldsAvailable")}
          </p>
        ) : (
          <Select
            value={data.field || NONE}
            onValueChange={(v) => onChange({ field: v === NONE ? "" : v })}
          >
            <SelectTrigger>
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>—</SelectItem>
              {availableFields.map((f) => (
                <SelectItem key={f.key} value={f.key}>
                  {f.label || f.key}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">
            {t("enterpriseProductBuilder.branchCasesLabel")}
          </Label>
          <Button size="sm" variant="outline" onClick={addCase} disabled={!data.field}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            {t("enterpriseProductBuilder.addCase")}
          </Button>
        </div>
        {cases.length === 0 && (
          <p className="rounded-md border border-dashed py-3 text-center text-xs text-muted-foreground">
            {t("enterpriseProductBuilder.noCasesYet")}
          </p>
        )}
        {cases.map((c, idx) => (
          <div key={c.id} className="space-y-2 rounded-md border bg-muted/30 p-3">
            <div className="grid grid-cols-[96px,1fr] gap-2">
              <Select
                value={c.op}
                onValueChange={(v) => updateCase(idx, { op: v as BranchOp })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OPS.map((op) => (
                    <SelectItem key={op} value={op}>
                      {op}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isEnum ? (
                <Select
                  value={String(c.value) || NONE}
                  onValueChange={(v) => updateCase(idx, { value: v === NONE ? "" : v })}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>—</SelectItem>
                    {options.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={String(c.value)}
                  onChange={(e) => updateCase(idx, { value: e.target.value })}
                  placeholder={t("enterpriseProductBuilder.caseValuePlaceholder")}
                  className="h-8 text-xs"
                />
              )}
            </div>
            <Input
              value={c.label ?? ""}
              onChange={(e) => updateCase(idx, { label: e.target.value })}
              placeholder={t("enterpriseProductBuilder.caseLabelPlaceholder")}
              className="h-8 text-xs"
            />
            <div className="flex items-center justify-between">
              <code className="text-[10px] text-muted-foreground">{c.id}</code>
              <Button
                size="sm"
                variant="ghost-destructive"
                className="h-7"
                onClick={() => removeCase(idx)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
      </div>

      <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
        {t("enterpriseProductBuilder.branchEdgeHint")}
      </p>
    </div>
  );
}
